import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import type {
  ChartSelectorConfig,
  SavedQueryDataType,
  SlotRole,
} from '@revelio/core';

export type { SavedQueryDataType };

async function fetchJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<T | undefined> {
  const csrfToken = getAuthDashMetaCsrfToken();

  const response = await fetch(input, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-surf-token-dash-meta': csrfToken ?? '',
      ...(init?.headers || {}),
    },
    ...init,
  });

  if (!response.ok) {
    const message = await response.text();
    const error: Error & { status?: number } = new Error(
      message || 'Unknown error'
    );
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) {
    return undefined;
  }

  return await response.json();
}

export type SavedQueryFieldDto = {
  name: string;
  type: 'string' | 'number' | 'string[]' | 'number[]';
  description?: string;
  required?: boolean;
  selectionLevelIds?: string[];
  fieldGroup?: string;
  extractMode?: 'id' | 'levelName';
  slotRole?: SlotRole;
  compatibleFilterIds?: string[];
};

export type SavedQueryDto = {
  id: string;
  name: string;
  description?: string;
  queryString?: string;
  fields: SavedQueryFieldDto[];
  dataType: SavedQueryDataType;
  reportId?: string;
  metricId?: string;
  selectorOverrides?: Partial<ChartSelectorConfig>;
};

export type ExecuteSavedQueryFieldValue = string | number | string[] | number[];

export const getSavedQueries = () =>
  fetchJson<SavedQueryDto[]>(
    `${DASH_META_ROOT}/api/custom-query/saved/report-builder`,
    {
      method: 'GET',
    }
  );

/** Full record including `queryString` (admin-only on dash-meta). */
export const getSavedQueryById = (savedQueryId: string) =>
  fetchJson<SavedQueryDto>(
    `${DASH_META_ROOT}/api/custom-query/saved/${encodeURIComponent(savedQueryId)}`,
    { method: 'GET' }
  );

export const executeSavedQuery = (
  savedQueryId: string,
  fieldValues: Record<string, ExecuteSavedQueryFieldValue>
) =>
  fetchJson<unknown[]>(`${DASH_META_ROOT}/api/custom-query/execute-saved`, {
    method: 'POST',
    body: JSON.stringify({ savedQueryId, fieldValues }),
  });
